import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input as ChakraInput,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Tag,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Input,
  Center,
  Spinner,
} from "@chakra-ui/react";

import { formatDate, IIntegration, useAuth } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import {
  getIntegrations,
  createIntegration,
  updateIntegration,
  deleteIntegration,
} from "./integrations-service";
import { MdIntegrationInstructions } from "react-icons/md";
import ProviderSelection, { ProviderOption } from "./provider-selection";
import { PageHeading, Panel, Table, TableRowActions } from "@bemap/ui";

const PROVIDER_OPTIONS: ProviderOption[] = [
  {
    id: "hotmart",
    name: "Hotmart",
    icon: MdIntegrationInstructions,
    color: "orange",
    description: "partner.integrations_hotmart_description",
    tokenLabel: "partner.integrations_token_label",
    tokenPlaceholder: "partner.integrations_token_placeholder",
    tokenHelperText: "partner.integrations_token_helper",
    showSecondaryToken: false,
    metadataFields: [
      {
        key: "addCredits",
        label: "partner.integrations_hotmart_add_credits",
        type: "number",
        placeholder: "partner.integrations_hotmart_add_credits_placeholder",
        helperText: "partner.integrations_hotmart_add_credits_helper",
        defaultValue: 0
      },
      // {
      //   key: "autoCreateUser",
      //   label: "partner.integrations_hotmart_auto_create_user",
      //   type: "checkbox",
      //   helperText: "partner.integrations_hotmart_auto_create_user_helper",
      //   defaultValue: true
      // },
      // {
      //   key: "autoCreateTeam",
      //   label: "partner.integrations_hotmart_auto_create_team",
      //   type: "checkbox",
      //   helperText: "partner.integrations_hotmart_auto_create_team_helper",
      //   defaultValue: true
      // }
    ]
  },
];

// Metadata Fields Component
const MetadataFields: FC<{
  provider: ProviderOption;
  metadata: Record<string, any>;
  onMetadataChange: (metadata: Record<string, any>) => void;
  textColor: string;
  background: string;
}> = ({ provider, metadata, onMetadataChange, textColor, background }) => {
  const { t } = useTranslation();

  const handleFieldChange = (key: string, value: any) => {
    const newMetadata = { ...metadata, [key]: value };
    onMetadataChange(newMetadata);
  };

  if (!provider.metadataFields || provider.metadataFields.length === 0) {
    return null;
  }

  return (
    <VStack spacing={4} align="stretch">
      <Text fontWeight="semibold" color={textColor}>
        {t("partner.integrations_advanced_settings")}
      </Text>
      {provider.metadataFields.map((field) => (
        <FormControl key={field.key}>
          <FormLabel>{t(field.label)}</FormLabel>
          {field.type === 'checkbox' ? (
            <Checkbox
              isChecked={metadata[field.key] ?? field.defaultValue}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
            >
              {field.helperText && (
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t(field.helperText)}
                </Text>
              )}
            </Checkbox>
          ) : field.type === 'textarea' ? (
            <Textarea
              color={textColor}
              background={background}
              placeholder={field.placeholder ? t(field.placeholder) : undefined}
              value={metadata[field.key] ?? field.defaultValue ?? ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              rows={3}
            />
          ) : (
            <Input
              color={textColor}
              background={background}
              type={field.type}
              placeholder={field.placeholder ? t(field.placeholder) : undefined}
              value={metadata[field.key] ?? field.defaultValue ?? ''}
              onChange={(e) => handleFieldChange(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
              required={field.required}
            />
          )}
          {field.helperText && field.type !== 'checkbox' && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {t(field.helperText)}
            </Text>
          )}
        </FormControl>
      ))}
    </VStack>
  );
};

// Webhook Endpoint Component
const WebhookEndpoint: FC<{
  integrationId: string;
  textColor: string;
  background: string;
}> = ({ integrationId, textColor, background }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const webhookUrl = `${import.meta.env.VITE_API_URL}/integrations/${integrationId}/webhook`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast({
        title: t("partner.integrations_webhook_copied"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t("partner.integrations_webhook_copy_error"),
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Text fontWeight="semibold" color={textColor}>
        {t("partner.integrations_webhook_endpoint")}
      </Text>
      <FormControl>
        <FormLabel>{t("partner.integrations_webhook_url_label")}</FormLabel>
        <HStack spacing={2}>
          <ChakraInput
            color={textColor}
            background={background}
            value={webhookUrl}
            isReadOnly
            fontSize="xs"
          />
          <Button
            size="md"
            onClick={handleCopy}
            colorScheme="blue"
            variant="outline"
          >
            {t("partner.integrations_webhook_copy")}
          </Button>
        </HStack>
        <Text fontSize="sm" color="gray.600" mt={1}>
          {t("partner.integrations_webhook_helper")}
        </Text>
      </FormControl>
    </VStack>
  );
};

const IntegrationsManagement = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();

  const teamId = parseInt(String(user?.currentTeamId));
  if (isNaN(teamId)) {
    toast({
      title: t("general.error"),
      description: "Invalid team ID",
      status: "error",
      isClosable: true,
    });
    return;
  }

  const initialIntegration: Omit<IIntegration, 'id' | 'created_at' | 'updated_at' | 'team'> = {
    name: '',
    team_id: teamId,
    provider: '',
    main_token: '',
    secondary_token: '',
    metadata: {},
  };

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">("new");
  const [integrations, setIntegrations] = useState<IIntegration[]>([]);
  const [integration, setIntegration] = useState<Omit<IIntegration, 'id' | 'created_at' | 'updated_at' | 'team'>>(initialIntegration);
  const [editingIntegration, setEditingIntegration] = useState<IIntegration | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderOption | null>(null);
  const [updateData, setUpdateData] = useState<string>("");

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  const columns = useMemo<ColumnDef<IIntegration, any>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: "name",
        header: t("partner.integrations_table_name"),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.provider,
        id: "provider",
        header: t("partner.integrations_table_provider"),
        cell: (info) => {
          const provider = PROVIDER_OPTIONS.find(p => p.id === info.getValue());
          return provider ? (
            <HStack spacing={2}>
              <Icon as={provider.icon} color={`${provider.color}.500`} />
              <Text>{provider.name}</Text>
            </HStack>
          ) : (
            <Tag size="sm" colorScheme="gray">
              {info.getValue()}
            </Tag>
          );
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(row.created_at ?? ""),
        id: "created_at",
        header: t("partner.integrations_table_created"),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.id,
        id: "webhook",
        header: t("partner.integrations_table_webhook"),
        cell: (info) => {
          const integrationId = info.getValue() as string;
          const baseUrl = window.location.origin;
          const webhookUrl = `${baseUrl}/integrations/webhook/${integrationId}`;

          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(webhookUrl);
              toast({
                title: t("partner.integrations_webhook_copied"),
                status: "success",
                duration: 2000,
                isClosable: true,
              });
            } catch (error) {
              toast({
                title: t("partner.integrations_webhook_copy_error"),
                status: "error",
                duration: 2000,
                isClosable: true,
              });
            }
          };

          return (
            <HStack spacing={2}>
              <Button
                size="xs"
                onClick={handleCopy}
                colorScheme="blue"
              >
                {t("partner.integrations_webhook_copy")}
              </Button>
            </HStack>
          );
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "actions",
        enableColumnFilter: false,
        enableHiding: true,
        header: t("partner.integrations_table_actions"),
        cell: (row) => {
          return (
            <TableRowActions
              editAction={() => handleEdit(row.getValue())}
              deleteAction={() => handleDelete(row.getValue())}
            />
          );
        },
      },
    ],
    [integrations, t]
  );

  useEffect(() => {
    if (integration.name.trim() && integration.provider && integration.main_token.trim()) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [integration]);

  useEffect(() => {
    loadIntegrations();
  }, [updateData]);

  useEffect(() => {
    if (integration.provider) {
      const provider = PROVIDER_OPTIONS.find(p => p.id === integration.provider);
      setSelectedProvider(provider || null);
    }
  }, [integration.provider]);

  function closeModal() {
    onClose();
    setIntegration(initialIntegration);
    setEditingIntegration(null);
    setSelectedProvider(null);
  }

  async function loadIntegrations() {
    setIsLoading(true);
    try {
      const response = await getIntegrations();
      setIntegrations(response.message);
      setIsLoading(false);
    } catch (error) {
      setIntegrations([]);
      setIsLoading(false);
      toast({
        title: t("partner.integrations_load_error"),
        status: "error",
        isClosable: true,
      });
    }
  }

  async function handleSave() {
    setIsSaving(true);
    toast({
      title: t("partner.integrations_saving"),
      status: "loading",
      isClosable: true,
    });

    try {
      if (modalType === "new") {
        await createIntegration(integration);
      } else if (modalType === "edit" && editingIntegration) {
        await updateIntegration(editingIntegration.id!, integration);
        if (!editingIntegration?.id) {
          throw new Error("Missing integration ID");
        }
        await updateIntegration(editingIntegration.id, integration);
      }

      toast.closeAll();
      toast({
        title: t("partner.integrations_save_success"),
        status: "success",
        isClosable: true,
      });

      setIsSaving(false);
      closeModal();
      setUpdateData(new Date().toLocaleString());
    } catch (error) {
      toast.closeAll();
      toast({
        title: t("partner.integrations_save_error"),
        status: "error",
        isClosable: true,
      });
      setIsSaving(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!editingIntegration) return;

    setIsDeleting(true);
    toast({
      title: t("partner.integrations_deleting"),
      status: "loading",
      isClosable: true,
    });

    try {
      if (!editingIntegration?.id) {
        throw new Error("Missing integration ID");
      }
      await deleteIntegration(editingIntegration.id);
      toast.closeAll();
      toast({
        title: t("partner.integrations_delete_success"),
        status: "success",
        isClosable: true,
      });
      closeModal();
      setUpdateData(new Date().toLocaleString());
    } catch (error) {
      toast.closeAll();
      toast({
        title: t("partner.integrations_delete_error"),
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleEdit(integrationData: IIntegration) {
    setEditingIntegration(integrationData);
    setIntegration({
      name: integrationData.name,
      team_id: integrationData.team_id,
      provider: integrationData.provider,
      main_token: integrationData.main_token,
      secondary_token: integrationData.secondary_token || '',
      metadata: integrationData.metadata || {},
    });
    setModalType("edit");
    onOpen();
  }

  function handleDelete(integrationData: IIntegration) {
    setEditingIntegration(integrationData);
    setModalType("delete");
    onOpen();
  }

  function handleProviderSelect(provider: ProviderOption) {
    setSelectedProvider(provider);

    // Initialize metadata with default values for the selected provider
    const defaultMetadata: Record<string, any> = {};
    if (provider.metadataFields) {
      provider.metadataFields.forEach(field => {
        defaultMetadata[field.key] = field.defaultValue;
      });
    }

    setIntegration({
      ...integration,
      provider: provider.id,
      metadata: defaultMetadata,
    });
  }

  function handleInputChange(field: keyof typeof integration, value: string) {
    setIntegration({
      ...integration,
      [field]: value,
    });
  }

  function RenderModal() {
    switch (modalType) {
      case "new":
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal} closeOnOverlayClick={false} size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {modalType === "new" ? t("partner.integrations_new") : t("partner.integrations_edit")}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>{t("partner.integrations_name_label")}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      placeholder={t("partner.integrations_name_placeholder")}
                      type="text"
                      value={integration.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </FormControl>

                  {modalType === "new" && (
                    <ProviderSelection
                      providers={PROVIDER_OPTIONS}
                      selectedProvider={selectedProvider}
                      onProviderSelect={handleProviderSelect}
                    />
                  )}

                  {selectedProvider && (
                    <>
                      <FormControl>
                        <FormLabel>{t(selectedProvider.tokenLabel)}</FormLabel>
                        <Input
                          color={textColor}
                          background={background}
                          placeholder={t(selectedProvider.tokenPlaceholder)}
                          type="text"
                          value={integration.main_token}
                          onChange={(e) => handleInputChange('main_token', e.target.value)}
                          required
                        />
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          {t(selectedProvider.tokenHelperText)}
                        </Text>
                      </FormControl>

                      {selectedProvider.showSecondaryToken && (
                        <FormControl>
                          <FormLabel>{t("partner.integrations_secondary_token_label")}</FormLabel>
                          <Textarea
                            color={textColor}
                            background={background}
                            placeholder={t("partner.integrations_secondary_token_placeholder")}
                            value={integration.secondary_token}
                            onChange={(e) => handleInputChange('secondary_token', e.target.value)}
                            rows={3}
                          />
                        </FormControl>
                      )}
                    </>
                  )}

                  {modalType === "edit" && editingIntegration?.id && (
                    <WebhookEndpoint
                      integrationId={editingIntegration.id}
                      textColor={textColor}
                      background={background}
                    />
                  )}

                  {selectedProvider && modalType === "new" && (
                    <MetadataFields
                      provider={selectedProvider}
                      metadata={integration.metadata ?? {}}
                      onMetadataChange={(newMetadata) => setIntegration({
                        ...integration,
                        metadata: newMetadata,
                      })}
                      textColor={textColor}
                      background={background}
                    />
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant="outline" onClick={closeModal}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={!isValid}
                >
                  {modalType === "new" ? t("actions.save") : t("actions.save")}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );

      case "delete":
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  {t("partner.integrations_delete")}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t("partner.integrations_delete_confirm", { name: editingIntegration?.name || "" })}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal} isDisabled={isDeleting}>
                    {t("actions.cancel")}
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={handleDeleteConfirm}
                    ml={3}
                    isLoading={isDeleting}
                    isDisabled={isDeleting}
                  >
                    {t("actions.delete")}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );

      default:
        return null;
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap={4} align="stretch" w="100%">
        <PageHeading title={t('partner.integrations')} actions={[
          {
            label: t('partner.integrations_new'),
            icon: <AddIcon />,
            onClick: () => {
              setModalType("new");
              onOpen();
            }
          }
        ]} />

        <Panel>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            integrations && (
              <Table
                tableId="integrations-table"
                columns={columns}
                data={integrations}
              />
            )
          )}
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default IntegrationsManagement; 